/*!
 * midiIn.js — MIDI from this browser's devices, as one event
 * © 2026 Rob Canning — GPLv3
 *
 * Web MIDI in -> window event `oscilla:midi-in`, detail
 *   { kind: "note"|"cc"|"pc", ch: 1-16, num, value, on: boolean, source }
 * which trig:midi listens for (animShared.js). The score's server relays a
 * pedal on its own machine as the same event (socket.js), and the score's
 * pedal map (system/midiInput.js) acts on it too.
 *
 * Timecode and clock (F0 F1 F2 F8 FA FB FC) are not events: they go to
 * whoever asked for them -- the score's timecode chase.
 *
 * Every message is a signal too, as a sound's level is:
 *   midi.note60    0..1 the velocity while the key is down, 0 when let go
 *   midi.cc64      0..1 the controller's value
 *   midi.pc3       1 for a moment on a program change
 *   midi3.cc64     the same, on channel 3 only
 * so follow(midi.cc1, 0, 1) turns a knob into a value, and a console
 * line's trig:midi, midi:note60 waits for it (midiTrigSignal).
 */

import { publish } from './paramBinding.js';

const TAG = '[midi-in]';
const HELD_MS = 100;                  // a key let go sooner still counts as down this long

let access = null;
const attached = new Set();
let last = null;                      // the last message, for the score's panel
const systemListeners = new Set();    // (bytes, timeStamp) -- timecode chase, the beat grid (clock(midi))
const onSystem = (bytes, at) => { for (const fn of systemListeners) fn(bytes, at); };
let onPorts = null;                   // a device came or went
let autoOpen = false;

/** Timecode / clock bytes go here too, stamped when they arrived. Returns: stop listening. */
export function onMidiSystem(fn) { systemListeners.add(fn); return () => systemListeners.delete(fn); }
/** Told when a device is plugged in (or out). */
export function onMidiPorts(fn) { onPorts = fn; }

export const lastMidiIn = () => last;
export const midiInputNames = () => (access ? [...access.inputs.values()].map(i => i.name) : []);

function emit(detail) {
    last = detail;
    window.dispatchEvent(new CustomEvent('oscilla:midi-in', { detail }));
}

function decode(bytes, source) {
    const [status, d1 = 0, d2 = 0] = bytes;
    const kind = status & 0xf0, ch = (status & 0x0f) + 1;
    if (kind === 0x90 || kind === 0x80) return { kind: 'note', ch, num: d1, value: d2, on: kind === 0x90 && d2 > 0, source };
    if (kind === 0xb0) return { kind: 'cc', ch, num: d1, value: d2, on: d2 >= 64, source };
    if (kind === 0xc0) return { kind: 'pc', ch, num: d1, value: 0, on: true, source };
    return null;
}

const SYSTEM = new Set([0xf0, 0xf1, 0xf2, 0xf8, 0xfa, 0xfb, 0xfc]);

/**
 * Listen to every MIDI input of this browser. sysex: timecode chase needs
 * it -- a DAW's locate is a full-frame SysEx. Safe to call again: a port is
 * listened to once.
 */
export async function openWebMidi({ sysex = false } = {}) {
    if (!navigator.requestMIDIAccess) return { ok: false, why: 'WebMIDI not available in this browser' };
    try {
        if (!access || (sysex && !access.sysexEnabled)) {
            const fresh = await navigator.requestMIDIAccess({ sysex }).catch(err => {
                if (!sysex) throw err;
                console.warn(`${TAG} SysEx refused (${err.message}): timecode runs, locates while stopped are not heard`);
                return access || navigator.requestMIDIAccess({ sysex: false });
            });
            if (fresh !== access) {
                // the old access's ports stop listening: two would hand every
                // message over twice (and a quarter frame twice breaks the set)
                access?.inputs.forEach(i => { i.onmidimessage = null; });
                access = fresh; attached.clear();
            }
        }
    } catch (err) {
        return { ok: false, why: `permission refused (${err.message})` };
    }
    const attach = (input) => {
        if (attached.has(input.id)) return;
        attached.add(input.id);
        input.onmidimessage = (e) => {
            if (SYSTEM.has(e.data?.[0])) { onSystem?.(e.data, e.timeStamp || performance.now()); return; }
            const m = decode(e.data, input.name); if (m) emit(m);
        };
        console.log(`${TAG} listening to "${input.name}"`);
    };
    access.inputs.forEach(attach);
    access.onstatechange = (e) => { if (e.port.type === 'input' && e.port.state === 'connected') attach(e.port); onPorts?.(); };
    return { ok: true, count: access.inputs.size };
}

/**
 * A page where a trig:midi cue should open the devices itself (Oscilla
 * Live). The score does not: a pedal on its server machine reaches every
 * client without each tablet asking for MIDI.
 */
export function setMidiAutoOpen(on) { autoOpen = !!on; }

// ---- the signals --------------------------------------------------------

// One state for all of MIDI, published whole: the bus keeps a source's last
// message in each 16 ms, so a chord sent key by key would else keep one key.
const state = { midi: {} };            // "midi" | "midi<ch>" -> { note60: v, ... }
const downAt = new Map();              // "midi3.note60" -> when it went down
let flushing = false;

function set(ch, name, v) {
    for (const uid of ['midi', `midi${ch}`]) (state[uid] ??= {})[name] = v;
}

// the messages of one moment (a chord) go out together
function publishSoon() {
    if (flushing) return;
    flushing = true;
    queueMicrotask(() => {
        flushing = false;
        for (const [uid, channels] of Object.entries(state)) publish('midi', uid, { ...channels });
    });
}

function toSignals({ kind, ch, num, value, on }) {
    const name = `${kind}${num}`, at = `midi${ch}.${name}`;
    if (kind === 'note' && on) {
        downAt.set(at, performance.now());
        set(ch, name, value / 127);
    } else if (kind === 'note') {
        // a drum pad lets go at once: down long enough for a trig: to see it
        const wait = HELD_MS - (performance.now() - (downAt.get(at) ?? -Infinity));
        if (wait > 0) { setTimeout(() => { set(ch, name, 0); publishSoon(); }, wait); return; }
        set(ch, name, 0);
    } else if (kind === 'cc') {
        set(ch, name, value / 127);
    } else {
        set(ch, name, 1);
        setTimeout(() => { set(ch, name, 0); publishSoon(); }, HELD_MS);
    }
    publishSoon();
}

// from this browser's devices, and from a pedal the server relays (socket.js)
window.addEventListener('oscilla:midi-in', ({ detail }) => { if (detail?.kind) toSignals(detail); });

/**
 * The signal a trig:midi waits for, as a condition: note60 -> "midi.note60",
 * cc64@1 -> "midi1.cc64>=0.5" (a controller is "on" from half way, as a
 * sustain pedal is). `spec` is parseMidiSpec's { kind, num, ch }.
 */
export function midiTrigSignal(spec) {
    const path = `midi${spec.ch ?? ''}.${spec.kind}${spec.num}`;
    return spec.kind === 'cc' ? `${path}>=0.5` : path;
}

/** A trig:midi cue is listening: open the devices, if this page says so. */
let opening = null;
export function wantMidiIn() {
    if (!autoOpen || access || opening) return;
    opening = openWebMidi().then(r => { if (!r.ok) console.log(`${TAG} ${r.why}`); opening = null; });
}
